import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Home,
  Share2,
  ShieldCheck,
  LogOut,
  ChevronRight,
  Sliders,
  Compass,
  Mail,
  ScrollText,
  FileText,
  Building2,
  Trash2,
  Sparkles,
  Crown,
  CreditCard,
  Shield,
  ExternalLink,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import DeleteAccountDialog from "../components/DeleteAccountDialog";
import { toast } from "sonner";
import { patchTours } from "../api/tourApi";
import { ProUpgradeButton } from "../components/billing/PaywallComponents";
import { createPortalSession, getBillingMe } from "../api/billingApi";

/**
 * Mobile-tab parity (native iOS now has a dedicated Profile tab — see
 * `mobile/app/(tabs)/_layout.tsx`). Web mobile-bottom-nav used to host
 * profile actions inside a slide-up sheet; this page replaces that so
 * the 5-tab bar can deep-link straight to a real route. Logout +
 * admin links live here so the bottom nav never has to host them
 * itself.
 *
 * The desktop top-nav is unaffected — desktop users can keep using
 * the top-bar Logout button.
 */
/**
 * Phase 3E — Membership-Management-Block für die Web-ProfilePage.
 *
 * Single source of truth for subscription state on web. iOS mobile
 * taps "Mitgliedschaft verwalten" in the native MembershipCard, which
 * opens `https://dealalert.ai/profile` via in-app Safari → the user
 * lands on this very ProfilePage and sees this block.
 *
 * 4 mutually-exclusive variants:
 *   • manual    — admin-granted Pro (no Stripe customer)        → info only, no CTA
 *   • paid      — active Stripe subscription                    → "Abo verwalten" → Customer Portal
 *   • trial     — running trial, not Pro yet                    → countdown + ProUpgradeButton
 *   • free      — no Pro, no trial (or expired)                 → ProUpgradeButton with feature teaser
 *
 * The block is ALWAYS rendered for logged-in users (no `md:` / Desktop
 * gating) — it must work for the mobile-web Apple-Reviewer flow too.
 */
function MembershipSection({ user }) {
  const { t, i18n } = useTranslation("profile");
  const [portalBusy, setPortalBusy] = useState(false);
  // Phase 1.2 — Apple awareness. /api/billing/me is the unified read
  // endpoint; we fetch it once on mount to know whether to render the
  // Apple variant. Stripe/Trial/Free/Manual/Admin variants remain
  // user-prop-driven (no behavioural change).
  const [billingState, setBillingState] = useState(null);
  const [billingLoaded, setBillingLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    getBillingMe()
      .then((data) => {
        if (alive) setBillingState(data || null);
      })
      .catch(() => {
        // Soft-fail: leave billingState null so the existing user-prop
        // logic decides the variant. No toast — this endpoint is new
        // and a 5xx must NOT regress the existing Stripe/Trial UX.
      })
      .finally(() => {
        if (alive) setBillingLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const onManage = useCallback(async () => {
    if (portalBusy) return;
    setPortalBusy(true);
    try {
      const { portal_url } = await createPortalSession();
      if (portal_url) window.location.href = portal_url;
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message || t("membership.manage_error");
      window.alert(msg);
      setPortalBusy(false);
    }
  }, [portalBusy, t]);

  const variant = useMemo(() => {
    const isPro = Boolean(user?.is_pro);
    const billingStatus = user?.billing_status || "none";
    const hasStripeCustomer = Boolean(user?.stripe_customer_id);
    const accessReason = user?.access_reason || null;
    // Phase 1.2 — Apple takes precedence over Stripe/Trial when the
    // unified resolver flagged this account as App-Store-managed. We
    // wait for /api/billing/me to load so we don't briefly render the
    // wrong variant on first paint; if the call fails or returns
    // managed_by=apple is absent, we fall through to the existing
    // logic and 0 behaviour changes.
    const managedBy = billingState?.managed_by || null;

    // 0. Admin role — checked first so admins never see anything else.
    if (user?.role === "admin") {
      return "admin";
    }
    // 1. Apple (Phase 1.2). Only flips when the backend resolver says so —
    //    requires apple_iap.expires_at in the future, see subscription_resolver.
    if (managedBy === "apple") {
      return "apple";
    }
    // 2. Admin-granted Pro (no Stripe involvement on this user).
    //    `billing_status === "manual"` is set by the Phase 3A admin
    //    grant-pro endpoint.
    if (isPro && (billingStatus === "manual" || accessReason === "manual_extension")) {
      return "manual";
    }
    // 3. Paid Pro (active subscription, has Stripe customer).
    if (isPro && hasStripeCustomer) {
      return "paid";
    }
    // 4. Trial active (is_pro true OR access_reason "trial").
    if (
      (isPro && accessReason === "trial") ||
      (!isPro && user?.trial_until && new Date(user.trial_until).getTime() > Date.now())
    ) {
      return "trial";
    }
    // 5. Free / expired.
    return "free";
  }, [user, billingState]);

  // Trial countdown ("Noch X Tage Y Std") — re-computed on render
  // since the section is mounted only when the user is on /profile.
  const trialCountdown = useMemo(() => {
    if (!user?.trial_until) return null;
    const diffMs = new Date(user.trial_until).getTime() - Date.now();
    if (diffMs <= 0) return t("membership.trial_expired");
    const days = Math.floor(diffMs / 86_400_000);
    const hours = Math.floor((diffMs % 86_400_000) / 3_600_000);
    if (days > 1) return t("membership.countdown_days", { count: days });
    if (days === 1) return t("membership.countdown_one_day", { hours });
    if (hours >= 1) return t("membership.countdown_hours", { hours });
    const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
    return t("membership.countdown_minutes", { minutes });
  }, [user?.trial_until, t]);

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(i18n.language || "de-AT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return null;
    }
  };

  // Pick visual accent + icon based on variant.
  const iconCls =
    variant === "admin" || variant === "manual" || variant === "paid" || variant === "apple"
      ? "text-emerald-300"
      : variant === "trial"
        ? "text-emerald-400"
        : "text-zinc-400";
  const Icon =
    variant === "admin"
      ? Shield
      : variant === "paid" || variant === "manual" || variant === "apple"
        ? Crown
        : variant === "trial"
          ? Sparkles
          : CreditCard;

  return (
    <section
      data-testid="profile-membership-block"
      className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-5"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10">
          <Icon size={20} strokeWidth={1.8} className={iconCls} aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            {t("membership.label")}
          </div>

          {variant === "admin" && (
            <>
              <div
                data-testid="profile-membership-plan"
                className="mt-1 text-base font-semibold text-zinc-100"
              >
                {t("membership.admin_title")}
              </div>
              <div className="mt-1 text-xs text-zinc-400">
                {t("membership.admin_desc")}
              </div>
              <div className="mt-1 text-[11px] text-zinc-500">
                {t("membership.admin_note")}
              </div>
            </>
          )}

          {variant === "apple" && (
            <div data-testid="profile-membership-state-apple">
              <div
                data-testid="profile-membership-plan"
                className="mt-1 text-base font-semibold text-zinc-100"
              >
                {t("membership.paid_title")}
              </div>
              <div
                data-testid="profile-membership-managed-source"
                className="mt-1 text-xs text-zinc-400"
              >
                {t("membership.apple_managed")}
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                {t("membership.apple_price")}
                {billingState?.current_period_end && (
                  <span>
                    {t("membership.apple_next_billing", {
                      date: formatDate(billingState.current_period_end),
                    })}
                  </span>
                )}
              </div>
              <div className="mt-1 text-[11px] text-zinc-500">
                {t("membership.apple_restore_note")}
              </div>
            </div>
          )}

          {variant === "manual" && (
            <>
              <div
                data-testid="profile-membership-plan"
                className="mt-1 text-base font-semibold text-zinc-100"
              >
                {t("membership.manual_title")}
              </div>
              <div className="mt-1 text-xs text-zinc-400">
                {user?.pro_access_until
                  ? t("membership.manual_desc_with_date", { date: formatDate(user.pro_access_until) })
                  : t("membership.manual_desc_no_date")}
              </div>
            </>
          )}

          {variant === "paid" && (
            <>
              <div
                data-testid="profile-membership-plan"
                className="mt-1 text-base font-semibold text-zinc-100"
              >
                {t("membership.paid_title")}
              </div>
              <div className="mt-1 text-xs text-zinc-400">
                {user?.current_period_end
                  ? t("membership.paid_next_billing", { date: formatDate(user.current_period_end) })
                  : user?.pro_access_until
                    ? t("membership.paid_until", { date: formatDate(user.pro_access_until) })
                    : t("membership.paid_active")}
              </div>
            </>
          )}

          {variant === "trial" && (
            <>
              <div
                data-testid="profile-membership-plan"
                className="mt-1 text-base font-semibold text-zinc-100"
              >
                {t("membership.trial_title")}
              </div>
              <div className="mt-1 text-xs text-emerald-300">
                {trialCountdown}
                {user?.trial_until && (
                  <span className="text-zinc-500">{t("membership.trial_until", { date: formatDate(user.trial_until) })}</span>
                )}
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                {t("membership.trial_note")}
              </div>
            </>
          )}

          {variant === "free" && (
            <>
              <div
                data-testid="profile-membership-plan"
                className="mt-1 text-base font-semibold text-zinc-100"
              >
                {t("membership.free_title")}
              </div>
              <ul className="mt-2 space-y-1 text-xs text-zinc-400">
                <li>• {t("membership.free_feature_1")}</li>
                <li>• {t("membership.free_feature_2")}</li>
                <li>• {t("membership.free_feature_3")}</li>
              </ul>
            </>
          )}
        </div>
      </div>

      {/* CTA row — variant decides which button (if any) appears. */}
      {variant === "apple" && (
        <div className="mt-4 flex justify-end">
          <a
            href="itms-apps://apps.apple.com/account/subscriptions"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="profile-membership-manage-apple-deeplink"
            className="inline-flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm font-medium text-amber-200 transition-colors hover:bg-amber-500/20"
          >
            {t("membership.apple_manage_cta")}
            <ExternalLink size={14} strokeWidth={1.8} aria-hidden="true" />
          </a>
        </div>
      )}

      {variant === "paid" && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            data-testid="profile-membership-cta"
            data-testid-variant="manage"
            onClick={onManage}
            disabled={portalBusy}
            className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-200 transition-colors hover:bg-emerald-500/20 disabled:opacity-60"
          >
            {portalBusy ? t("membership.manage_busy") : t("membership.manage_cta")}
          </button>
        </div>
      )}

      {(variant === "trial" || variant === "free") && (
        <div className="mt-4 flex justify-end">
          <ProUpgradeButton data-testid="profile-membership-cta">
            {variant === "trial" ? t("membership.upgrade_trial") : t("membership.upgrade_free")}
          </ProUpgradeButton>
        </div>
      )}
    </section>
  );
}

function Row({ to, onClick, icon: Icon, label, tone, testId, trailing }) {
  const base =
    "flex items-center gap-3 rounded-lg px-4 py-3 text-sm transition-colors";
  const toneCls =
    tone === "danger"
      ? "text-rose-300 hover:bg-rose-500/10 border-rose-500/30"
      : "text-zinc-200 hover:bg-zinc-900 border-zinc-800";
  const inner = (
    <>
      <Icon size={18} strokeWidth={1.8} aria-hidden="true" className="shrink-0" />
      <span className="flex-1 text-left">{label}</span>
      {trailing ?? (
        <ChevronRight size={16} className="text-zinc-600" aria-hidden="true" />
      )}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        onClick={onClick}
        data-testid={testId}
        className={`${base} border ${toneCls}`}
      >
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`${base} w-full border ${toneCls}`}
    >
      {inner}
    </button>
  );
}

export default function ProfilePage() {
  const { t } = useTranslation("profile");
  const { user, isAdmin, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const onLogout = useCallback(async () => {
    // Navigate to landing BEFORE clearing auth state — otherwise the
    // ProtectedRoute on this page redirects to /login first.
    navigate("/", { replace: true });
    await logout();
  }, [logout, navigate]);

  // TourGuide — re-arm all tours (deck / heatmap / KI coach mark).
  const onRestartTour = useCallback(async () => {
    try {
      await patchTours({
        tour_deck_done: false,
        tour_heatmap_done: false,
        tour_ai_done: false,
        tour_home_done: false,
      });
      window.localStorage.removeItem("da_tour_home_v1");
      await refreshUser();
      toast.success(t("actions.restart_tour_done"));
      navigate("/");
    } catch {
      toast.error(t("actions.restart_tour_failed"));
    }
  }, [refreshUser, navigate, t]);

  if (!user) {
    // Hard-belt-and-braces guard; AppRoutes wraps this in ProtectedRoute
    // already, so this should never render.
    return null;
  }

  return (
    <div data-testid="profile-page" className="space-y-6">
      {/* Account header */}
      <section
        data-testid="profile-account-card"
        className="rounded-2xl border border-zinc-800 bg-zinc-950/60 px-5 py-4"
      >
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          {t("account.label")}
        </div>
        <div
          data-testid="profile-email"
          className="mt-1 truncate text-base font-medium text-zinc-100"
        >
          {user.email}
        </div>
        {user.first_name || user.last_name ? (
          <div className="mt-0.5 text-xs text-zinc-400">
            {[user.first_name, user.last_name].filter(Boolean).join(" ")}
          </div>
        ) : null}
        <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-zinc-800 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-400">
          {isAdmin ? t("account.badge_admin") : user.is_pro ? t("account.badge_pro") : t("account.badge_free")}
        </div>
      </section>

      {/* Phase 3E — prominenter Membership-Management-Block.
          Single source of truth on web for the subscription state.
          See `MembershipSection` definition above for the 4-variant
          state machine and the iOS-tap-from-mobile rationale. */}
      <MembershipSection user={user} />

      {/* Master-Sprint P1 — unified language: the app language (header
          switcher) automatically drives the AI language. The separate
          AI-language selector was removed to avoid double selection. */}

      {/* V4.2 P0 — Referral UI has moved to /share-and-earn as the
          single canonical surface. The Profile row below is the ONLY
          referral entry point (no duplicated QR / share / copy widgets
          here anymore). */}

      {/* Primary actions */}
      <section className="space-y-2">
        <Row
          to="/"
          icon={Home}
          label={t("actions.home")}
          testId="profile-link-home"
        />
        <Row
          to="/share-and-earn"
          icon={Share2}
          label={t("actions.share_earn")}
          testId="profile-link-share-earn"
        />
        <Row
          to="/preferences"
          icon={Sliders}
          label={t("actions.preferences")}
          testId="profile-link-preferences"
        />
        <Row
          onClick={onRestartTour}
          icon={Compass}
          label={t("actions.restart_tour")}
          testId="profile-restart-tour"
        />
      </section>

      {/* Admin link — only for admin users (mirrors native iOS, where
          admin tools live inside the Profile screen, not as a tab). */}
      {isAdmin && (
        <section data-testid="profile-admin-section" className="space-y-2">
          <div className="px-1 text-[10px] uppercase tracking-widest text-zinc-500">
            {t("admin_section.label")}
          </div>
          <Row
            to="/admin"
            icon={ShieldCheck}
            label={t("admin_section.console")}
            testId="profile-link-admin"
          />
        </section>
      )}

      {/* Legal */}
      <section data-testid="profile-legal-section" className="space-y-2">
        <div className="px-1 text-[10px] uppercase tracking-widest text-zinc-500">
          {t("legal_section.label")}
        </div>
        <Row
          to="/agb"
          icon={ScrollText}
          label={t("legal_section.agb")}
          testId="profile-link-agb"
        />
        <Row
          to="/datenschutz"
          icon={FileText}
          label={t("legal_section.privacy")}
          testId="profile-link-datenschutz"
        />
        <Row
          to="/impressum"
          icon={Building2}
          label={t("legal_section.impressum")}
          testId="profile-link-impressum"
        />
        <Row
          to="#"
          icon={Mail}
          label="info@dealalert.ai"
          onClick={(e) => {
            e.preventDefault();
            window.location.href = "mailto:info@dealalert.ai";
          }}
          testId="profile-link-contact"
        />
      </section>

      {/* Sign out */}
      <section>
        <Row
          icon={LogOut}
          label={t("logout.label")}
          onClick={onLogout}
          tone="danger"
          testId="profile-logout"
          trailing={<span className="text-[10px] uppercase text-rose-300/70">Sign out</span>}
        />
      </section>

      {/* Danger zone — In-App Account Deletion (Apple Guideline 5.1.1(v)) */}
      <section data-testid="profile-danger-section" className="space-y-2 pt-4">
        <div className="px-1 text-[10px] uppercase tracking-widest text-rose-400/80">
          {t("danger.label")}
        </div>
        <Row
          icon={Trash2}
          label={t("danger.delete_account")}
          onClick={() => setDeleteOpen(true)}
          tone="danger"
          testId="profile-delete-account"
          trailing={<span className="text-[10px] uppercase text-rose-300/70">{t("danger.delete_trailing")}</span>}
        />
        <p className="px-1 pt-1 text-[11px] text-zinc-500">
          {t("danger.delete_hint")}
        </p>
      </section>

      <DeleteAccountDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </div>
  );
}
